import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  Sun, 
  Zap, 
  Trash2,
  RotateCcw,
  CheckCircle2,
  Home
} from 'lucide-react';

const scTranslations = {
  de: {
    titlePre: 'Solar ', titleHighlight: 'Konfigurator',
    subtitle: 'Klicken Sie auf das Dach um Module zu platzieren • Verschieben Sie sie frei',
    clickHere: '👆 Klicken Sie hier', modules: 'Module',
    delete: 'Löschen', removeAll: 'Alle entfernen', orientation: 'Ausrichtung',
    dirSouth: 'Süd', dirEast: 'Ost', dirWest: 'West', dirNorth: 'Nord',
    result: 'Ergebnis', placeModules: 'Module auf dem Dach platzieren',
    system: 'Anlage', production: 'Produktion', savings: 'Ersparnis',
    prodUnit: 'kWh/Jahr', savingsUnit: '€/Jahr', co2Unit: 'kg/Jahr',
    investment: 'Investition', amortization: 'Amortisation', years: 'Jahre',
    freeQuote: 'Kostenloses Angebot'
  },
  en: {
    titlePre: 'Solar ', titleHighlight: 'Configurator',
    subtitle: 'Click on the roof to place modules • Move them freely',
    clickHere: '👆 Click here', modules: 'Modules',
    delete: 'Delete', removeAll: 'Remove All', orientation: 'Orientation',
    dirSouth: 'South', dirEast: 'East', dirWest: 'West', dirNorth: 'North',
    result: 'Result', placeModules: 'Place modules on the roof',
    system: 'System', production: 'Production', savings: 'Savings',
    prodUnit: 'kWh/year', savingsUnit: '€/year', co2Unit: 'kg/year',
    investment: 'Investment', amortization: 'Payback', years: 'years',
    freeQuote: 'Free Quote'
  }
};

const SolarConfigurator = () => {
  const { language } = useLanguage();
  const t = scTranslations[language] || scTranslations.de;
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [orientation, setOrientation] = useState('south');

  const PANEL_W = 32;
  const PANEL_H = 50;

  const handleRoofClick = (e) => {
    if (dragging !== null) return;
    if (e.target.closest('.solar-panel')) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - PANEL_W / 2;
    const y = e.clientY - rect.top - PANEL_H / 2;
    
    // Bounds check
    if (x >= 0 && x <= 420 - PANEL_W && y >= 0 && y <= 180 - PANEL_H) {
      setPanels([...panels, { x, y, id: Date.now() }]);
      setSelectedPanel(panels.length);
    }
  };

  const handlePanelMouseDown = (e, index) => {
    e.stopPropagation();
    setDragging(index);
    setSelectedPanel(index);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (dragging === null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    let x = e.clientX - rect.left - dragOffset.x;
    let y = e.clientY - rect.top - dragOffset.y;
    
    // Keep within bounds
    x = Math.max(0, Math.min(x, 420 - PANEL_W));
    y = Math.max(0, Math.min(y, 180 - PANEL_H));
    
    setPanels(panels.map((p, i) => i === dragging ? { ...p, x, y } : p));
  };

  const handleMouseUp = () => setDragging(null);

  const deleteSelected = () => {
    if (selectedPanel !== null) {
      setPanels(panels.filter((_, i) => i !== selectedPanel));
      setSelectedPanel(null);
    }
  };

  const clearAll = () => {
    setPanels([]);
    setSelectedPanel(null);
  };

  // Calculations
  const systemSize = (panels.length * 400) / 1000;
  const factors = { south: 1, east: 0.85, west: 0.85, north: 0.6 };
  const production = Math.round(systemSize * 950 * factors[orientation]);
  const savings = Math.round(production * 0.35 * 0.7 + production * 0.3 * 0.08);
  const co2 = Math.round(production * 0.4);
  const cost = Math.round(systemSize * 1400);
  const payback = cost > 0 && savings > 0 ? (cost / savings).toFixed(1) : 0;

  return (
    <section id="solar-configurator" className="py-16 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-3">
            <Home className="w-4 h-4" />
            Drag & Drop
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            {t.titlePre}<span className="text-amber-400">{t.titleHighlight}</span>
          </h2>
          <p className="text-gray-400">{t.subtitle}</p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Card className="bg-slate-800/60 border-slate-700 overflow-hidden">
            <CardContent className="p-6">
              <div className="grid lg:grid-cols-3 gap-6">
                
                {/* House */}
                <div className="lg:col-span-2">
                  <div 
                    className="relative rounded-xl overflow-hidden"
                    style={{ 
                      height: '450px',
                      background: 'linear-gradient(180deg, #1e3a5f 0%, #3b82f6 30%, #7dd3fc 70%, #bae6fd 100%)'
                    }}
                  >
                    {/* Sun */}
                    <div 
                      className="absolute top-6 right-10 w-16 h-16 rounded-full"
                      style={{ 
                        background: 'radial-gradient(circle, #fef9c3 0%, #fde047 50%, #f59e0b 100%)',
                        boxShadow: '0 0 60px rgba(251,191,36,0.5)'
                      }}
                    />
                    
                    {/* Clouds */}
                    <div className="absolute top-8 left-8 text-5xl opacity-90">☁️</div>
                    <div className="absolute top-6 left-1/3 text-4xl opacity-80">☁️</div>
                    <div className="absolute top-12 right-1/3 text-3xl opacity-70">☁️</div>
                    
                    {/* House Container */}
                    <div className="absolute bottom-16 left-1/2 -translate-x-1/2">
                      
                      {/* Roof - Clickable */}
                      <div 
                        className="relative mx-auto cursor-crosshair"
                        style={{ 
                          width: '460px', 
                          height: '180px',
                          background: 'linear-gradient(180deg, #4b5563 0%, #374151 50%, #1f2937 100%)',
                          clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)'
                        }}
                        onClick={handleRoofClick}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                      >
                        {/* Roof texture */}
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 12px, rgba(255,255,255,0.1) 12px, rgba(255,255,255,0.1) 13px)'
                        }} />
                        
                        {/* Panels */}
                        {panels.map((panel, index) => (
                          <div
                            key={panel.id}
                            className={`solar-panel absolute cursor-move ${selectedPanel === index ? 'z-20' : 'z-10'}`}
                            style={{ left: panel.x + 20, top: panel.y + 30, width: PANEL_W, height: PANEL_H }}
                            onMouseDown={(e) => handlePanelMouseDown(e, index)}
                          >
                            <div 
                              className={`w-full h-full rounded-sm ${selectedPanel === index ? 'ring-2 ring-blue-400' : ''}`}
                              style={{
                                background: 'linear-gradient(145deg, #171717 0%, #0a0a0a 50%, #171717 100%)',
                                border: '2px solid #333',
                                boxShadow: '0 3px 10px rgba(0,0,0,0.5)'
                              }}
                            >
                              <div className="w-full h-full grid grid-cols-2 grid-rows-4 gap-px p-1">
                                {[...Array(8)].map((_, i) => (
                                  <div key={i} className="bg-gray-800 rounded-[2px]" />
                                ))}
                              </div>
                              <div className="absolute top-1 left-1 w-3 h-4 bg-white/10 rounded-sm" />
                            </div>
                          </div>
                        ))}
                        
                        {/* Help text */}
                        {panels.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center pt-16">
                            <div className="bg-black/60 text-white px-4 py-2 rounded-lg text-sm">
                              {t.clickHere}
                            </div>
                          </div>
                        )}
                        
                        {/* Count badge */}
                        {panels.length > 0 && (
                          <div className="absolute top-4 right-8 bg-amber-500 text-black px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                            {panels.length} {t.modules}
                          </div>
                        )}
                      </div>
                      
                      {/* House Body */}
                      <div className="relative mx-auto -mt-1" style={{ width: '380px' }}>
                        <div className="bg-stone-100 h-36 border-t-4 border-gray-700">
                          {/* Windows */}
                          <div className="flex justify-between px-10 pt-5">
                            <div className="w-14 h-16 bg-sky-400 border-4 border-gray-700 grid grid-cols-2 grid-rows-2">
                              <div className="border-r border-b border-gray-600" />
                              <div className="border-b border-gray-600" />
                              <div className="border-r border-gray-600" />
                              <div />
                            </div>
                            <div className="w-14 h-16 bg-sky-400 border-4 border-gray-700 grid grid-cols-2 grid-rows-2">
                              <div className="border-r border-b border-gray-600" />
                              <div className="border-b border-gray-600" />
                              <div className="border-r border-gray-600" />
                              <div />
                            </div>
                          </div>
                          {/* Door */}
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-24 bg-amber-800 border-4 border-amber-900 rounded-t-lg">
                            <div className="absolute right-2 top-1/2 w-3 h-3 bg-yellow-400 rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Ground */}
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-green-700 to-green-500">
                      <div className="flex justify-between items-end h-full px-6 pb-2">
                        <span className="text-3xl">🌲</span>
                        <span className="text-2xl">🌳</span>
                        <span className="text-3xl">🌲</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <Button onClick={deleteSelected} disabled={selectedPanel === null} variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20 disabled:opacity-30">
                      <Trash2 className="w-4 h-4 mr-2" />{t.delete}
                    </Button>
                    <Button onClick={clearAll} disabled={panels.length === 0} variant="outline"
                      className="border-slate-600 text-gray-400 hover:bg-slate-700 disabled:opacity-30">
                      <RotateCcw className="w-4 h-4 mr-2" />{t.removeAll}
                    </Button>
                  </div>
                </div>

                {/* Results */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-400" />{t.orientation}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { k: 'south', l: t.dirSouth, p: '100%' },
                        { k: 'east', l: t.dirEast, p: '85%' },
                        { k: 'west', l: t.dirWest, p: '85%' },
                        { k: 'north', l: t.dirNorth, p: '60%' }
                      ].map((d) => (
                        <button key={d.k} onClick={() => setOrientation(d.k)}
                          className={`py-3 rounded-lg text-sm font-bold transition ${orientation === d.k ? 'bg-amber-500 text-black' : 'bg-slate-700 text-gray-300 hover:bg-slate-600'}`}>
                          {d.l} <span className={`text-xs ${orientation === d.k ? 'text-amber-800' : 'text-gray-500'}`}>({d.p})</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-slate-700 pt-4">
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-400" />{t.result}
                    </h3>
                    
                    {panels.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Sun className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">{t.placeModules}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex justify-between bg-slate-700/50 rounded-lg p-3">
                          <span className="text-gray-400">{t.system}</span>
                          <span className="text-white font-bold">{systemSize.toFixed(1)} kWp</span>
                        </div>
                        <div className="flex justify-between bg-slate-700/50 rounded-lg p-3">
                          <span className="text-gray-400">{t.production}</span>
                          <span className="text-amber-400 font-bold">{production.toLocaleString()} {t.prodUnit}</span>
                        </div>
                        <div className="flex justify-between bg-slate-700/50 rounded-lg p-3">
                          <span className="text-gray-400">{t.savings}</span>
                          <span className="text-green-400 font-bold">{savings.toLocaleString()} {t.savingsUnit}</span>
                        </div>
                        <div className="flex justify-between bg-slate-700/50 rounded-lg p-3">
                          <span className="text-gray-400">CO₂</span>
                          <span className="text-emerald-400 font-bold">{co2.toLocaleString()} {t.co2Unit}</span>
                        </div>
                        
                        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg p-4 mt-3">
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-300">{t.investment}</span>
                            <span className="text-white font-bold">{cost.toLocaleString()} €</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">{t.amortization}</span>
                            <span className="text-green-400 font-bold">{payback} {t.years}</span>
                          </div>
                        </div>
                        
                        <Button onClick={() => window.location.href = '/beratung'}
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold mt-4 py-6">
                          <CheckCircle2 className="w-5 h-5 mr-2" />{t.freeQuote}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SolarConfigurator;
